import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  FileText,
  Calendar,
  Download,
  ChevronDown,
  ChevronRight,
  BarChart2,
  X,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import api from "~/services/api";
import CashflowModal from "./cashflowModal";

const dateTypeOptions = [
  { value: "accrual_date", label: "Accrual Date" },
  { value: "settlement_date", label: "Settlement Date" },
  { value: "due_date", label: "Due Date" },
];

function CashflowRow({
  label,
  values = [],
  children = [],
  level = 0,
  onOpenChartIcon,
  onOpenCell,
  expandedRows,
  setExpandedRows,
  parentLabel = null,
  showVariation = false,
  isRangePairs = false,
  pairCount = 0,
  variation_amount = null,
  variation_percentage = null,
}) {
  const isExpanded = expandedRows.includes(label);
  const hasChildren = children && children.length > 0;

  const handleToggleExpand = () => {
    setExpandedRows((prev) =>
      prev.includes(label) ? prev.filter((r) => r !== label) : [...prev, label]
    );
  };

  return (
    <>
      <tr className={`${level === 0 ? "bg-gray-100" : "bg-white"}`}>
        <td
          className="px-2 py-2 text-xs font-semibold sticky left-0 z-10 bg-inherit"
          style={{ paddingLeft: `${level * 20 + 8}px` }}
        >
          <div className="flex items-center gap-1">
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown
                  size={14}
                  className="cursor-pointer text-mila_orange"
                  onClick={handleToggleExpand}
                />
              ) : (
                <ChevronRight
                  size={14}
                  className="cursor-pointer text-mila_orange"
                  onClick={handleToggleExpand}
                />
              )
            ) : null}
            {level === 0 ? (
              <BarChart2
                size={14}
                className="text-mila_orange cursor-pointer"
                onClick={() => onOpenChartIcon(label, values)}
              />
            ) : null}
            <span>{label}</span>
          </div>
        </td>

        {isRangePairs
          ? (
            Array.from({ length: pairCount || 0 }).map((_, pIdx) => {
              const idxA = pIdx * 2;
              const idxB = pIdx * 2 + 1;
              const cols = values || [];
              const renderCell = (val, i) => {
                const cellInteractive = typeof val === "number";
                return (
                  <td
                    className={`px-2 py-2 text-xs text-right border-l ${cellInteractive ? "cursor-pointer hover:bg-blue-100" : "cursor-default"}`}
                    onClick={() => cellInteractive && onOpenCell(label, values, i, parentLabel)}
                  >
                    {val == null
                      ? "N/A"
                      : (typeof val === "number"
                        ? Number(val).toLocaleString("en-US")
                        : "")}
                  </td>
                );
              };
              return (
                <React.Fragment key={`row-pair-${pIdx}`}>
                  {renderCell(cols[idxA], idxA)}
                  {renderCell(cols[idxB], idxB)}
                  {showVariation && (
                    <>
                      <td className="px-2 py-2 text-xs text-right border-l bg-yellow-50">
                        {Array.isArray(variation_amount)
                          ? (variation_amount[pIdx] != null
                            ? Number(variation_amount[pIdx]).toLocaleString("en-US")
                            : "N/A")
                          : ""}
                      </td>
                      <td className="px-2 py-2 text-xs text-right border-l bg-yellow-50">
                        {Array.isArray(variation_percentage)
                          ? (variation_percentage[pIdx] != null
                            ? `${Number(variation_percentage[pIdx]).toFixed(2)}%`
                            : "N/A")
                          : ""}
                      </td>
                    </>
                  )}
                </React.Fragment>
              );
            })
          )
          : (
            <>
              {(values || []).map((v, i) => {
                const num = Number(v);
                const cellInteractive = Number.isFinite(num);
                return (
                  <td
                    key={i}
                    className={`px-2 py-2 text-xs text-right border-l ${cellInteractive ? "cursor-pointer hover:bg-blue-100" : "cursor-default"}`}
                    onClick={() => cellInteractive && onOpenCell(label, values, i, parentLabel)}
                  >
                    {v == null || v === "" ? "" : Number(num || 0).toLocaleString("en-US")}
                  </td>
                );
              })}
              {showVariation && (
                <>
                  <td className="px-2 py-2 text-xs text-right border-l bg-yellow-50">
                    {Array.isArray(variation_amount)
                      ? (variation_amount[0] != null
                        ? Number(variation_amount[0]).toLocaleString("en-US")
                        : "N/A")
                      : ""}
                  </td>
                  <td className="px-2 py-2 text-xs text-right border-l bg-yellow-50">
                    {Array.isArray(variation_percentage)
                      ? (variation_percentage[0] != null
                        ? `${Number(variation_percentage[0]).toFixed(2)}%`
                        : "N/A")
                      : ""}
                  </td>
                </>
              )}
            </>
          )}
      </tr>

      {isExpanded &&
        children.map((child, idx) => (
          <CashflowRow
            key={idx}
            label={child.label}
            values={child.values}
            children={child.children}
            level={level + 1}
            onOpenChartIcon={onOpenChartIcon}
            onOpenCell={onOpenCell}
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            parentLabel={label}
            showVariation={showVariation}
            isRangePairs={isRangePairs}
            pairCount={pairCount}
            variation_amount={child.variation_amount}
            variation_percentage={child.variation_percentage}
          />
        ))}
    </>
  );
}

export default function CashflowReport() {
  const filial = useSelector((state) => state.auth?.filial);
  const currentYear = new Date().getFullYear();
  const baseYearOptions = [currentYear - 2, currentYear - 1, currentYear];
  const compareYearOptions = [currentYear - 3, currentYear - 2, currentYear - 1, currentYear];
  const [cashflowConfigs, setCashflowConfigs] = useState([]);
  const [selectedCashflowConfig, setSelectedCashflowConfig] = useState("");
  const [dateType, setDateType] = useState("due_date");
  const [periodType, setPeriodType] = useState("annual_monthly");
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [selectedQuarter, setSelectedQuarter] = useState(1);
  const [columns, setColumns] = useState([]);
  const [cashflowData, setCashflowData] = useState([]);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const [chartModal, setChartModal] = useState(null);
  const [cfModalOpen, setCfModalOpen] = useState(false);
  const [cfModalData, setCfModalData] = useState(null);
  const [momRangeMode, setMomRangeMode] = useState(false);
  const [compareMonth, setCompareMonth] = useState(0);
  const [compareYear, setCompareYear] = useState(currentYear - 1);
  const [baseEndMonth, setBaseEndMonth] = useState(2);
  const [yoyRangeMode, setYoyRangeMode] = useState(false);
  const [variationEnabled, setVariationEnabled] = useState(true);
  const [variationMeta, setVariationMeta] = useState(null);

  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  async function fetchCashflowConfigs() {
    try {
      const { data } = await api.get("/cashflowconfigs-getall");
      setCashflowConfigs(data || []);
      if (data?.length > 0) {
        setSelectedCashflowConfig(data[0].id);
        setDateType(data[0]?.date_type || "due_date");
      }
    } catch {
      setCashflowConfigs([]);
    }
  }

  function findNetCashflowValues(children = []) {
    for (let c of children) {
      if (c.label?.toLowerCase().includes("net") && c.label?.toLowerCase().includes("cashflow"))
        return c.values || [];
      const nested = findNetCashflowValues(c.children || []);
      if (nested.length > 0) return nested;
    }
    return [];
  }

  function mapAccount(acc) {
    return {
      label: acc.code ? `${acc.code} ${acc.name}` : acc.name,
      values: acc.values || [],
      variation_amount: acc.variation_amount || null,
      variation_percentage: acc.variation_percentage || null,
      children: (acc.children || []).map(mapAccount),
    };
  }

  function mapSubsection(sub) {
    return {
      label: sub.label,
      values: sub.values || [],
      variation_amount: sub.variation_amount || null,
      variation_percentage: sub.variation_percentage || null,
      children: (sub.accounts || []).map(mapAccount),
    };
  }

  function mapSection(section) {
    const children = (section.subsections || []).map(mapSubsection);
    let sectionValues = section.values || [];
    if (/operating activities|investing activities|financing activities/i.test(section.label)) {
      const netVals = findNetCashflowValues(children);
      if (netVals.length > 0) sectionValues = netVals;
    }
    return {
      label: section.label,
      values: sectionValues,
      variation_amount: section.variation_amount || null,
      variation_percentage: section.variation_percentage || null,
      children
    };
  }

  async function fetchData() {
    if (!selectedCashflowConfig) return;
    setLoading(true);
    try {
      let url = "";
      let params = {};

      if (
        periodType === "specific_month" ||
        periodType === "annual_monthly" ||
        periodType === "quarter" ||
        periodType === "annual_accumulated"
      ) {
        url = "/cashflowconfigs/period";
        params = {
          cashflow_config_id: selectedCashflowConfig,
          period_type: periodType,
          year: selectedYear,
        };
        if (periodType === "specific_month") {
          params.month = selectedMonth;
        }
        if (periodType === "quarter") {
          params.quarter = selectedQuarter;
        }
      } else if (periodType === "mom") {

        url = "/cashflowconfigs/compare-mom";
        params = {
          cashflow_config_id: selectedCashflowConfig,
          base_year: selectedYear,
          base_month: selectedMonth,
        };
        if (momRangeMode) {
          params.base_end_month = baseEndMonth;
          if (compareYear !== selectedYear - 1) {
            params.compare_year = compareYear;
          }
        } else {
          params.compare_year = compareYear;
          params.compare_month = compareMonth;
        }
      } else if (periodType === "yoy") {
        if (yoyRangeMode) {
          url = "/cashflowconfigs/compare-yoy-range";
          params = {
            cashflow_config_id: selectedCashflowConfig,
            base_year: selectedYear,
            base_month: selectedMonth + 1,
            base_end_month: baseEndMonth + 1,
          };
          if (compareYear !== selectedYear - 1) {
            params.compare_year = compareYear;
          }
        } else {
          url = "/cashflowconfigs/compare-yoy";
          params = {
            cashflow_config_id: selectedCashflowConfig,
          };
          if (selectedYear) params.base_year = selectedYear;
          if (compareYear) params.compare_year = compareYear;
        }
      }

      if (dateType) {
        params.date_type = dateType;
      }

     
      console.log("[Cashflow Report] id_filial:", filial?.id, filial);

      const { data } = await api.get(url, { params });

      const periodHeaders =
        (Array.isArray(data.periods) && data.periods.length > 0)
          ? data.periods
          : (Array.isArray(data.headers) && data.headers.length > 0)
            ? data.headers
            : (Array.isArray(data.period_headers) && data.period_headers.length > 0)
              ? data.period_headers
              : (Array.isArray(data.columns) && data.columns.length > 0)
                ? data.columns
                : [];
      setColumns(periodHeaders);
      setTitle(data.period_label || "Cashflow Statement");
      setCashflowData((data.sections || []).map(mapSection));
      setVariationMeta(data.variation_meta || null);
    } catch {
      setCashflowData([]);
      setColumns([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCashflowConfigs();
  }, [filial?.id]);

  useEffect(() => {
    if (filial && filial.id != null) {
      console.log("[Cashflow Report] filial selecionada:", filial.id, filial);
    }
  }, [filial?.id]);

  useEffect(() => {
    if (selectedCashflowConfig) fetchData();
  }, [
    selectedCashflowConfig,
    periodType,
    selectedYear,
    selectedMonth,
    selectedQuarter,
    compareMonth,
    compareYear,
    momRangeMode,
    baseEndMonth,
    yoyRangeMode,
    dateType,
    filial?.id,
  ]);

  useEffect(() => {
    if (!selectedCashflowConfig || !Array.isArray(cashflowConfigs) || cashflowConfigs.length === 0) return;
    const cfg = cashflowConfigs.find((c) => String(c.id) === String(selectedCashflowConfig));
    if (!cfg) return;
    const cfgDateType = cfg.date_type || "due_date";
    setDateType(cfgDateType);
  }, [selectedCashflowConfig, cashflowConfigs]);

  useEffect(() => {
    if (momRangeMode && periodType === "mom") {
      setCompareYear(selectedYear - 1);
    }
  }, [momRangeMode, periodType, selectedYear]);

  useEffect(() => {
    if (yoyRangeMode && periodType === "yoy") {
      setCompareYear(selectedYear - 1);
    }
  }, [yoyRangeMode, periodType, selectedYear]);


  useEffect(() => {
    if (periodType === "specific_month") {

      if (selectedMonth < 1 || selectedMonth > 12) setSelectedMonth(1);
    } else if (periodType === "mom") {

      if (selectedMonth < 0 || selectedMonth > 11) setSelectedMonth(0);
    }
  }, [periodType]);

  const variationAllowed = (periodType === "yoy" && yoyRangeMode) || (periodType === "mom" && momRangeMode);
  const showVariation = variationAllowed && variationEnabled;
  const variationHeaders = showVariation
    ? (variationMeta?.headers
      || variationMeta?.columns
      || variationMeta?.titles
      || variationMeta?.labels
      || [])
    : [];
  const pairCountMeta = Number(variationMeta?.pair_count) || 0;
  const computedPairCount = pairCountMeta > 0
    ? pairCountMeta
    : (Array.isArray(columns) ? Math.floor(columns.length / 2) : 0);
  const isRangePairs = (
    (variationMeta?.placement === "after_each_pair" && computedPairCount > 1)
    || (((periodType === "mom" && momRangeMode) || (periodType === "yoy" && yoyRangeMode)) && computedPairCount > 1)
  );

  const handleOpenChartModal = (label, values) => {
    if ((columns || []).length <= 1) return;
    const data = (columns || []).map((c, i) => ({
      name: c,
      value1: Number(values?.[i] || 0),
    }));
    setChartModal({ label, data });
  };

  const monthNameToNumber = {
    "Jan": 1, "Feb": 2, "Mar": 3, "Apr": 4, "May": 5, "Jun": 6,
    "Jul": 7, "Aug": 8, "Sep": 9, "Oct": 10, "Nov": 11, "Dec": 12,
    "January": 1, "February": 2, "March": 3, "April": 4, "June": 6,
    "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12,
  };

  const handleOpenCashflowModal = (label, columnsValues, columnIndex, parentLabel = null) => {
    const rawPeriod = columns[columnIndex] || "-";
    const value = columnsValues?.[columnIndex] || 0;
    let normalizedPeriod = rawPeriod;
    const isLastColumn = columnIndex === columns.length - 1;

    if ((periodType === "annual_monthly" || periodType === "annual_accumulated") && isLastColumn) {
      normalizedPeriod = `totalyear=${selectedYear}`;
    }
    if (periodType === "quarter" && isLastColumn) {
      normalizedPeriod = `totalquarter=Q${selectedQuarter}-${selectedYear}`;
    }
    if (periodType === "mom") {
      if (momRangeMode) {
        
        const monthMatch = String(rawPeriod).match(/^(\d{4})-(\d{2})$/);
        if (monthMatch) {
          const year = monthMatch[1];
          const month = monthMatch[2];
          normalizedPeriod = `${year}-${month}`;
        }
      }
    } else if (periodType === "yoy") {
      if (yoyRangeMode) {
       
        const rangeMatch = String(rawPeriod).match(/^(\d{4})-([A-Za-z]+)-([A-Za-z]+)$/);
        if (rangeMatch) {
          const year = rangeMatch[1];
          const startMonthName = rangeMatch[2];
          const endMonthName = rangeMatch[3];
          const startMonthNum = monthNameToNumber[startMonthName];
          const endMonthNum = monthNameToNumber[endMonthName];
          if (startMonthNum && endMonthNum) {
            normalizedPeriod = `yoyrange=${year}-${startMonthNum}-${endMonthNum}`;
          }
        }
      } else {
        const yearMatch = String(rawPeriod).match(/^\d{4}$/);
        if (yearMatch) {
          normalizedPeriod = `year${yearMatch[0]}`;
        }
      }
    }


    let inferredType = null;
    const codeMatch = (label || "").match(/^[\d.]+/);
    const hasCode = !!codeMatch;
    if (!hasCode) {
      const parent = String(parentLabel || "");
      const full = `${parent} ${label}`.toLowerCase();


      if (/^\s*operating\s+activities\s*$/i.test(label)) {
        inferredType = "operating_activities";
      } else if (/(^|\s)operating\s+revenues(\s|$)/i.test(label) || /operating\s+revenues/i.test(parent)) {
        inferredType = "operating_revenues";
      } else if (/(^|\s)operating\s+expenses(\s|$)/i.test(label) || /operating\s+expenses/i.test(parent)) {
        inferredType = "operating_expenses";
      }


      else if (/^\s*investing\s+activities\s*$/i.test(label)) {
        inferredType = "investing_activities";
      } else if (/(^|\s)investing\s+revenues(\s|$)/i.test(label) || /investing\s+revenues/i.test(parent)) {
        inferredType = "investing_revenues";
      } else if (/(^|\s)capex(\s|$)/i.test(label) || /(^|\s)capex(\s|$)/i.test(parent)) {
        inferredType = "capex";
      }


      else if (/^\s*financing\s+activities\s*$/i.test(label)) {
        inferredType = "financing_activities";
      } else if (/(financing\s+inflows)/i.test(full)) {
        inferredType = "financing_inflows";
      } else if (/(financing\s+outflows)/i.test(full)) {
        inferredType = "financing_outflows";
      }
    }

    const modalData = {
      accountLabel: label,
      period: normalizedPeriod,
      value: value,
      type: inferredType,
      cashflowConfigId: selectedCashflowConfig,
      dateType: dateType,
    };

    setCfModalData(modalData);
    setCfModalOpen(true);
  };

  return (
    <div className="w-full h-full flex flex-col bg-white border overflow-hidden rounded-r-xl">
      <div className="bg-gray-100 px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText size={20} className="text-gray-700" />
          <h2 className="text-lg font-bold text-gray-700">CashFlow Report</h2>
        </div>
        <button className="px-3 py-1.5 bg-mila_orange text-white rounded-sm text-xs flex items-center gap-1 hover:bg-orange-600">
          <Download size={14} /> Export
        </button>
      </div>

      <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap gap-3 items-end justify-between">
        <div className="flex flex-wrap gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600">Cashflow Config</label>
            <select
              value={selectedCashflowConfig}
              onChange={(e) => setSelectedCashflowConfig(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
            >
              {cashflowConfigs.map((cfg) => (
                <option key={cfg.id} value={cfg.id}>
                  {cfg.cashflow_name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600">Date Type</label>
            <select
              value={dateType}
              onChange={(e) => setDateType(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
            >
              {dateTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600">Period Type</label>
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
            >
              <option value="annual_monthly">Annual Month by Month</option>
              <option value="specific_month">Specific Month</option>
              <option value="quarter">Quarter</option>
              <option value="annual_accumulated">Annual Accumulated</option>
              <option value="mom">MoM (Month vs Month)</option>
              <option value="yoy">YoY (Year vs Year)</option>
            </select>
          </div>

          {(periodType === "specific_month" || periodType === "annual_monthly" || periodType === "annual_accumulated" || periodType === "quarter") && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
              >
                {baseYearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          )}

          {periodType === "specific_month" && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {String(m).padStart(2, "0")}
                  </option>
                ))}
              </select>
            </div>
          )}

          {periodType === "quarter" && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Quarter</label>
              <select
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(Number(e.target.value))}
                className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
              >
                {[1, 2, 3, 4].map((q) => (
                  <option key={q} value={q}>
                    Q{q}
                  </option>
                ))}
              </select>
            </div>
          )}

          {periodType === "mom" && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">{momRangeMode ? "Start Month" : "Base Month"}</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {months.map((m, idx) => (
                    <option key={idx} value={idx}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              {momRangeMode && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-gray-600">End Month</label>
                  <select
                    value={baseEndMonth}
                    onChange={(e) => setBaseEndMonth(Number(e.target.value))}
                    className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                  >
                    {months.map((m, idx) => (
                      <option key={idx} value={idx} disabled={idx < selectedMonth}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Base Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {baseYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {!momRangeMode && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-gray-600">Compare Month</label>
                  <select
                    value={compareMonth}
                    onChange={(e) => setCompareMonth(Number(e.target.value))}
                    className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                  >
                    {months.map((m, idx) => (
                      <option key={idx} value={idx}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">{momRangeMode ? "Compare Year (Optional)" : "Compare Year"}</label>
                <select
                  value={compareYear}
                  onChange={(e) => setCompareYear(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {compareYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">&nbsp;</label>
                <label className="flex items-center gap-2 cursor-pointer h-[30px]">
                  <input
                    type="checkbox"
                    checked={momRangeMode}
                    onChange={(e) => setMomRangeMode(e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded-sm focus:ring-orange-500"
                  />
                  <span className="text-xs font-medium text-gray-700">Range Mode</span>
                </label>
              </div>
            </>
          )}

          {periodType === "yoy" && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Base Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {baseYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {yoyRangeMode && (
                <>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600">Start Month</label>
                    <select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(Number(e.target.value))}
                      className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                    >
                      {months.map((m, idx) => (
                        <option key={idx} value={idx}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600">End Month</label>
                    <select
                      value={baseEndMonth}
                      onChange={(e) => setBaseEndMonth(Number(e.target.value))}
                      className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                    >
                      {months.map((m, idx) => (
                        <option key={idx} value={idx} disabled={idx < selectedMonth}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">{yoyRangeMode ? "Compare Year (Optional)" : "Compare Year"}</label>
                <select
                  value={compareYear}
                  onChange={(e) => setCompareYear(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {compareYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">&nbsp;</label>
                <label className="flex items-center gap-2 cursor-pointer h-[30px]">
                  <input
                    type="checkbox"
                    checked={yoyRangeMode}
                    onChange={(e) => setYoyRangeMode(e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded-sm focus:ring-orange-500"
                  />
                  <span className="text-xs font-medium text-gray-700">Range Mode</span>
                </label>
              </div>
            </>
          )}
        </div>

        {variationAllowed && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600">Show Columns</label>
            <div className="flex items-center gap-3 h-[30px]">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={variationEnabled}
                  onChange={(e) => setVariationEnabled(e.target.checked)}
                  className="w-4 h-4 text-yellow-600 border-gray-300 rounded-sm focus:ring-yellow-500"
                />
                <span className="text-xs font-medium text-gray-700">Variation</span>
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
          <Calendar size={14} />
          <span>Period: {title}</span>
        </div>

        <div className="overflow-x-auto mb-6">
          <table className="w-full border-collapse min-w-max">
            <thead className="sticky top-0 bg-gray-200 z-10">
              <tr>
                <th className="px-2 py-2 text-xs font-semibold text-left border sticky left-0 bg-gray-200 z-20">
                  Description
                </th>
                {isRangePairs
                  ? (
                    Array.from({ length: computedPairCount }).map((_, pIdx) => (
                      <React.Fragment key={`pair-${pIdx}`}>
                        <th className="px-2 py-2 text-xs font-semibold text-right border w-28">
                          {columns[pIdx * 2]}
                        </th>
                        <th className="px-2 py-2 text-xs font-semibold text-right border w-28">
                          {columns[pIdx * 2 + 1]}
                        </th>
                        {variationHeaders.map((header, hIdx) => (
                          <th key={`var-${pIdx}-${hIdx}`} className="px-2 py-2 text-xs font-semibold text-right border bg-yellow-100 w-28">
                            {header}
                          </th>
                        ))}
                      </React.Fragment>
                    ))
                  )
                  : (
                    <>
                      {columns.map((col, idx) => (
                        <th key={idx} className="px-2 py-2 text-xs font-semibold text-right border w-28">
                          {col}
                        </th>
                      ))}
                      {showVariation && variationHeaders.map((header, idx) => (
                        <th key={`var-${idx}`} className="px-2 py-2 text-xs font-semibold text-right border bg-yellow-100 w-28">{header}</th>
                      ))}
                    </>
                  )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-2 py-4 text-xs text-gray-500" colSpan={
                    isRangePairs
                      ? 1 + computedPairCount * (2 + variationHeaders.length)
                      : 1 + columns.length + variationHeaders.length
                  }>
                    Loading...
                  </td>
                </tr>
              ) : cashflowData.length === 0 ? (
                <tr>
                  <td className="px-2 py-4 text-xs text-gray-500" colSpan={
                    isRangePairs
                      ? 1 + computedPairCount * (2 + variationHeaders.length)
                      : 1 + columns.length + variationHeaders.length
                  }>
                    No data
                  </td>
                </tr>
              ) : (
                cashflowData.map((sec, idx) => (
                  <CashflowRow
                    key={idx}
                    label={sec.label}
                    values={sec.values}
                    children={sec.children}
                    expandedRows={expandedRows}
                    setExpandedRows={setExpandedRows}
                    onOpenChartIcon={handleOpenChartModal}
                    onOpenCell={handleOpenCashflowModal}
                    showVariation={showVariation}
                    isRangePairs={isRangePairs}
                    pairCount={computedPairCount}
                    variation_amount={sec.variation_amount}
                    variation_percentage={sec.variation_percentage}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {chartModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-[750px] h-[400px] relative p-4 flex flex-col">
            <button
              className="absolute right-3 top-3 text-gray-500 hover:text-black"
              onClick={() => setChartModal(null)}
            >
              <X size={18} />
            </button>
            <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <BarChart2 className="text-mila_orange" size={16} />
              {chartModal.label}
            </h3>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartModal.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value1" fill="#EE5827" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <CashflowModal
        isOpen={cfModalOpen}
        onClose={() => setCfModalOpen(false)}
        modalData={cfModalData}
      />
    </div>
  );
}
